/**
 * VoiceOrb - Animated fluid sphere component for voice interaction
 * States: idle (breathing), listening (reactive ripples), thinking (rotating), speaking (pulsing)
 */

import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { Mic, MicOff, Volume2, Loader2 } from 'lucide-react';
import type { VoiceState } from '../hooks/useVoiceMode';

interface VoiceOrbProps {
    state: VoiceState;
    audioLevel: number;
    remainingSeconds: number;
    isTimeLimitReached: boolean;
    onStartListening: () => void;
    onStopListening: () => void;
    onStopSpeaking: () => void;
    className?: string;
}

export function VoiceOrb({
    state,
    audioLevel,
    remainingSeconds,
    isTimeLimitReached,
    onStartListening,
    onStopListening,
    onStopSpeaking,
    className
}: VoiceOrbProps) {
    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const stateConfig = useMemo(() => {
        switch (state) {
            case 'listening':
                return {
                    label: 'Listening...',
                    icon: <Mic className="w-8 h-8 text-white" />,
                    gradient: 'from-emerald-400 via-teal-500 to-cyan-500',
                    shadowColor: 'shadow-emerald-500/50',
                    pulseScale: 1 + audioLevel * 0.3,
                };
            case 'thinking':
                return {
                    label: 'Processing...',
                    icon: <Loader2 className="w-8 h-8 text-white animate-spin" />,
                    gradient: 'from-amber-400 via-orange-500 to-yellow-500',
                    shadowColor: 'shadow-amber-500/50',
                    pulseScale: 1,
                };
            case 'speaking':
                return {
                    label: 'Speaking...',
                    icon: <Volume2 className="w-8 h-8 text-white" />,
                    gradient: 'from-violet-400 via-purple-500 to-fuchsia-500',
                    shadowColor: 'shadow-violet-500/50',
                    pulseScale: 1 + audioLevel * 0.25,
                };
            default:
                return {
                    label: 'Tap to speak',
                    icon: <Mic className="w-8 h-8 text-white/80" />,
                    gradient: 'from-slate-400 via-slate-500 to-slate-600',
                    shadowColor: 'shadow-slate-500/30',
                    pulseScale: 1,
                };
        }
    }, [state, audioLevel]);

    const handleClick = () => {
        if (isTimeLimitReached) return;

        switch (state) {
            case 'idle':
                onStartListening();
                break;
            case 'listening':
                onStopListening();
                break;
            case 'speaking':
                onStopSpeaking();
                break;
            default:
                break;
        }
    };

    const isWarning = remainingSeconds <= 60 && remainingSeconds > 0;

    return (
        <div className={clsx('flex flex-col items-center gap-4', className)}>
            {/* Main Orb Container */}
            <div className="relative">
                {/* Outer glow rings */}
                <AnimatePresence>
                    {state === 'listening' && (
                        <>
                            {[0, 1, 2].map((i) => (
                                <motion.div
                                    key={`ring-${i}`}
                                    className={clsx(
                                        'absolute inset-0 rounded-full',
                                        'bg-gradient-to-r from-emerald-400/20 to-teal-400/20'
                                    )}
                                    initial={{ scale: 1, opacity: 0.6 }}
                                    animate={{
                                        scale: [1, 1.5 + i * 0.3, 2 + i * 0.4],
                                        opacity: [0.6 - i * 0.15, 0.3 - i * 0.1, 0],
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        delay: i * 0.4,
                                        ease: 'easeOut',
                                    }}
                                />
                            ))}
                        </>
                    )}

                    {state === 'speaking' && (
                        <>
                            {[0, 1, 2].map((i) => (
                                <motion.div
                                    key={`speak-ring-${i}`}
                                    className={clsx(
                                        'absolute inset-0 rounded-full',
                                        'bg-gradient-to-r from-violet-400/20 to-purple-400/20'
                                    )}
                                    initial={{ scale: 1, opacity: 0.5 }}
                                    animate={{
                                        scale: [1, 1.3 + audioLevel * 0.5],
                                        opacity: [0.5, 0],
                                    }}
                                    transition={{
                                        duration: 0.8,
                                        repeat: Infinity,
                                        delay: i * 0.25,
                                        ease: 'easeOut',
                                    }}
                                />
                            ))}
                        </>
                    )}
                </AnimatePresence>

                {/* Main Orb Button */}
                <motion.button
                    onClick={handleClick}
                    disabled={isTimeLimitReached || state === 'thinking'}
                    className={clsx(
                        'relative w-32 h-32 rounded-full',
                        'flex items-center justify-center',
                        'transition-all duration-300',
                        'focus:outline-none focus:ring-4 focus:ring-emerald-500/30',
                        isTimeLimitReached 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'cursor-pointer hover:shadow-2xl',
                        stateConfig.shadowColor,
                        'shadow-xl'
                    )}
                    animate={{
                        scale: stateConfig.pulseScale,
                    }}
                    transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 20,
                    }}
                    whileHover={!isTimeLimitReached && state === 'idle' ? { scale: 1.05 } : {}}
                    whileTap={!isTimeLimitReached ? { scale: 0.95 } : {}}
                >
                    {/* Gradient background */}
                    <motion.div
                        className={clsx(
                            'absolute inset-0 rounded-full',
                            'bg-gradient-to-br',
                            stateConfig.gradient
                        )}
                        animate={state === 'thinking' ? { rotate: 360 } : { rotate: 0 }}
                        transition={
                            state === 'thinking'
                                ? { duration: 3, repeat: Infinity, ease: 'linear' }
                                : { duration: 0 }
                        }
                    />

                    {/* Inner glow */}
                    <div className="absolute inset-2 rounded-full bg-white/10 backdrop-blur-sm" />

                    {/* Fluid morphing overlay */}
                    <motion.div
                        className={clsx(
                            'absolute inset-4 rounded-full',
                            'bg-gradient-to-tl from-white/30 to-transparent'
                        )}
                        animate={
                            state === 'listening'
                                ? {
                                      scale: [1, 1.1 + audioLevel * 0.2, 1],
                                      rotate: [0, 5, -5, 0],
                                  }
                                : state === 'speaking'
                                ? {
                                      scale: [1, 1.05 + audioLevel * 0.15, 1],
                                      opacity: [0.3, 0.5 + audioLevel * 0.3, 0.3],
                                  }
                                : state === 'idle'
                                ? {
                                      scale: [1, 1.02, 1],
                                      opacity: [0.3, 0.4, 0.3],
                                  }
                                : {}
                        }
                        transition={{
                            duration: state === 'idle' ? 3 : 0.5,
                            repeat: Infinity,
                            ease: 'easeInOut',
                        }}
                    />

                    {/* Center icon */}
                    <div className="relative z-10">{stateConfig.icon}</div>

                    {/* Audio level visualizer bars (for listening state) */}
                    {state === 'listening' && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
                            {[0.3, 0.6, 1, 0.6, 0.3].map((multiplier, i) => (
                                <motion.div
                                    key={`bar-${i}`}
                                    className="w-1 bg-white/80 rounded-full"
                                    animate={{
                                        height: 4 + audioLevel * 16 * multiplier,
                                    }}
                                    transition={{ duration: 0.1 }}
                                />
                            ))}
                        </div>
                    )}
                </motion.button>

                {/* Thinking spinner ring */}
                {state === 'thinking' && (
                    <motion.div
                        className="absolute inset-0 rounded-full border-4 border-transparent border-t-amber-300"
                        animate={{ rotate: 360 }}
                        transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: 'linear',
                        }}
                    />
                )}
            </div>

            {/* State Label */}
            <motion.p
                className={clsx(
                    'text-sm font-medium',
                    state === 'idle' ? 'text-slate-500' : 'text-slate-700'
                )}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                key={state}
            >
                {isTimeLimitReached ? 'Time limit reached' : stateConfig.label}
            </motion.p>

            {/* Time Remaining */}
            <div
                className={clsx(
                    'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium',
                    isTimeLimitReached
                        ? 'bg-red-100 text-red-700'
                        : isWarning
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-600'
                )}
            >
                {isTimeLimitReached ? (
                    <>
                        <MicOff className="w-3.5 h-3.5" />
                        <span>Voice unavailable</span>
                    </>
                ) : (
                    <>
                        <Mic className="w-3.5 h-3.5" />
                        <span>{formatTime(remainingSeconds)} remaining</span>
                    </>
                )}
            </div>

            {/* Instructions */}
            <AnimatePresence>
                {state === 'idle' && !isTimeLimitReached && (
                    <motion.p
                        className="text-xs text-slate-400 text-center max-w-[200px]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        Click the orb to start speaking. Click again to send your message.
                    </motion.p>
                )}
                {state === 'listening' && (
                    <motion.p
                        className="text-xs text-emerald-600 text-center max-w-[200px]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        Speak now. Click again when you're done.
                    </motion.p>
                )}
                {state === 'speaking' && (
                    <motion.p
                        className="text-xs text-violet-600 text-center max-w-[200px]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        Agent is responding. Click to interrupt.
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    );
}

export default VoiceOrb;




